<script>
import { defineAsyncComponent, ref } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import MobileTopbar from 'next/sidebar/MobileTopbar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

// import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

export default {
  components: {
    NextSidebar,
    MobileTopbar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    // CopilotContainer,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      displayLayoutType: '',
      isSidebarOpen: false,
      isMobileViewport: false,
    };
  },
  computed: {
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
    previouslyUsedSidebarView() {
      const { previously_used_sidebar_view: showSecondarySidebar } =
        this.uiSettings;
      return showSecondarySidebar;
    },
  },
  watch: {
    $route() {
      // Cerrar el drawer del sidebar al navegar en móvil
      this.isSidebarOpen = false;
    },
    isSidebarOpen(isOpen) {
      // Bloquear el scroll del fondo mientras el drawer está abierto en móvil
      this.setBodyScrollLock(isOpen && this.isMobileViewport);
    },
    displayLayoutType() {
      const { LAYOUT_TYPES } = wootConstants;
      this.updateUISettings({
        conversation_display_type:
          this.displayLayoutType === LAYOUT_TYPES.EXPANDED
            ? LAYOUT_TYPES.EXPANDED
            : this.previouslyUsedDisplayType,
        show_secondary_sidebar:
          this.displayLayoutType === LAYOUT_TYPES.EXPANDED
            ? false
            : this.previouslyUsedSidebarView,
      });
    },
  },
  mounted() {
    this.handleResize();
    this.updateViewport();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('resize', this.updateViewport);
    window.addEventListener('keydown', this.handleKeydown);
  },
  unmounted() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('resize', this.updateViewport);
    window.removeEventListener('keydown', this.handleKeydown);
    this.setBodyScrollLock(false);
  },

  methods: {
    openSidebar() {
      this.isSidebarOpen = true;
      // Mover el foco dentro del drawer al abrirlo (solo móvil)
      if (this.isMobileViewport) {
        this.$nextTick(() => {
          document.getElementById('main-sidebar')?.focus();
        });
      }
    },
    closeSidebar() {
      this.isSidebarOpen = false;
      // Devolver el foco al botón hamburguer al cerrar (solo móvil)
      if (this.isMobileViewport) {
        this.$nextTick(() => {
          document.getElementById('sidebar-toggle')?.focus();
        });
      }
    },
    handleKeydown(e) {
      if (e.key === 'Escape' && this.isSidebarOpen) {
        this.closeSidebar();
      }
    },
    updateViewport() {
      const { SIDEBAR_BREAKPOINT } = wootConstants;
      this.isMobileViewport = window.innerWidth < SIDEBAR_BREAKPOINT;
      // Al pasar a desktop el sidebar es fijo: reseteamos el estado del drawer
      // para que no reaparezca abierto al volver a un ancho móvil.
      if (!this.isMobileViewport && this.isSidebarOpen) {
        this.closeSidebar();
      }
    },
    setBodyScrollLock(shouldLock) {
      document.body.classList.toggle('overflow-hidden', shouldLock);
    },
    handleResize() {
      const { SMALL_SCREEN_BREAKPOINT, LAYOUT_TYPES } = wootConstants;
      let throttled = false;
      const delay = 150;

      if (throttled) {
        return;
      }
      throttled = true;

      setTimeout(() => {
        throttled = false;
        if (window.innerWidth <= SMALL_SCREEN_BREAKPOINT) {
          this.displayLayoutType = LAYOUT_TYPES.EXPANDED;
        } else {
          this.displayLayoutType = LAYOUT_TYPES.CONDENSED;
        }
      }, delay);
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col lg:flex-row lg:flex-wrap app-wrapper text-n-slate-12"
  >
    <MobileTopbar
      :is-open="isSidebarOpen"
      @toggle="openSidebar"
      @show-create-account-modal="openCreateAccountModal"
    />
    <NextSidebar
      :is-open="isSidebarOpen"
      :is-mobile="isMobileViewport"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close="closeSidebar"
    />
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isSidebarOpen"
        class="fixed inset-0 z-40 bg-n-alpha-black1 backdrop-blur-[2px] lg:hidden"
        aria-hidden="true"
        @click="closeSidebar"
      />
    </Transition>
    <main class="flex flex-1 min-h-0 lg:h-full px-0 overflow-hidden">
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      />
      <template v-if="!showUpgradePage">
        <router-view />
        <CommandBar />
        <!-- <CopilotContainer /> -->
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>
